import { NullValue, Readout, StatusChip, type StatusTone } from '@saniplus/ui';
import type { ReactNode } from 'react';

/**
 * The panel eyebrow and the label → value rows every W8 facts panel is made of
 * (`W8-recovery/JUDGEMENT.md` §6.3 `FactList` / `FactRow`).
 *
 * Values are right-aligned and mono with `tabular-nums`, so a column of times
 * lines up and a supervisor reading the phone over a worker's shoulder can
 * compare them. Labels truncate; **values never do** — the value is the fact.
 *
 * "Never truncate" is not "never wrap", and the difference is the whole of
 * `prose`. A clock time is four characters and must stay on one line. A site
 * name is « Complexe de transformation et de conditionnement agroalimentaire
 * de Delta — Bâtiment Ouest », and holding *that* on one line pushed it clean
 * off a 390 px screen and squeezed its own label to nothing (QA-2, W3 blocked
 * state, demo tenant). So a mono value never shrinks and a prose value wraps.
 *
 * Candidate for `packages/ui`: this is the same anatomy the admin detail
 * panels use, and it is listed as such in this feature's README.
 */
export interface PanelHeadingProps {
  /** The eyebrow, already translated. Rendered at `label` scale, uppercase. */
  readonly title: string;
  /** A status chip on the right — BLOQUANT, HAE, À VÉRIFIER. */
  readonly chip?: { readonly tone: StatusTone; readonly label: string };
}

export function PanelHeading({ title, chip }: PanelHeadingProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-label uppercase text-ink-muted">{title}</h2>
      {chip === undefined ? null : <StatusChip tone={chip.tone}>{chip.label}</StatusChip>}
    </div>
  );
}

export interface Fact {
  readonly key: string;
  /** Already translated. */
  readonly label: string;
  /** A formatted value, or `null` to render the payroll-critical dash. */
  readonly value: ReactNode | null;
  /**
   * What the missing value means, already translated — required whenever
   * `value` is `null`, because a bare dash announces as nothing at all.
   */
  readonly nullLabel?: string;
  /** Renders the value in sans rather than mono — a name, a site. */
  readonly prose?: boolean;
  readonly tone?: 'default' | 'danger';
}

export interface FactListProps {
  readonly facts: readonly Fact[];
  readonly className?: string;
}

export function FactList({ facts, className }: FactListProps) {
  return (
    <dl className={className}>
      {facts.map((fact) => (
        <div
          key={fact.key}
          className="flex items-baseline justify-between gap-3 border-t border-hairline py-2 first:border-t-0 first:pt-0"
        >
          <dt className="min-w-0 truncate text-body-compact text-ink-muted">{fact.label}</dt>
          <dd className={fact.prose === true ? 'min-w-0 text-end' : 'shrink-0 text-end'}>
            {fact.value === null ? (
              // D-12 on the worker surface: never `0 h 00`, never blank — a gap
              // in danger ink with a sentence a screen reader can announce.
              <NullValue label={fact.nullLabel ?? fact.label} showLabel />
            ) : fact.prose === true ? (
              <span
                className={
                  fact.tone === 'danger'
                    ? 'text-subject break-words text-status-danger-ink'
                    : 'text-subject break-words text-ink'
                }
              >
                {fact.value}
              </span>
            ) : (
              <Readout scale="data" tone={fact.tone === 'danger' ? 'danger' : 'default'}>
                {fact.value}
              </Readout>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
